import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { Box, Typography } from "@mui/material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export interface HrGraphData {
  label: string;
  completionRate: number;
  totalActivities: number;
  completedActivities: number;
}

interface Props {
  data: HrGraphData[];
  title: string;
  activityLabel: string;
  chartType?: "bar" | "doughnut";
  showLabels?: boolean;
}

const HrStatsGraphs: React.FC<Props> = ({
  data,
  title,
  activityLabel,
  chartType = "bar",
  showLabels = false,
}) => {
  const labels = data.map((d) => d.label);

  const isDoughnut = chartType === "doughnut" && data.length === 1;

  // Custom plugin to show values above bars
  const barDataLabelsPlugin = {
    id: "barDataLabels",
    afterDatasetsDraw(chart: any) {
      if (!showLabels) return;
      const { ctx, data } = chart;
      ctx.save();
      data.datasets.forEach((dataset: any, i: number) => {
        const meta = chart.getDatasetMeta(i);
        if (meta.type !== "bar") return;
        meta.data.forEach((bar: any, index: number) => {
          const value = dataset.data[index];
          ctx.font = "bold 12px sans-serif";
          ctx.fillStyle = "#444";
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillText(`${value}%`, bar.x, bar.y - 5);
        });
      });
      ctx.restore();
    },
  };

  const completionRateData = isDoughnut 
    ? {
        labels: ["Completed", "Remaining"],
        datasets: [
          {
            data: [data[0].completionRate, 100 - data[0].completionRate],
            backgroundColor: ["rgba(75, 192, 192, 0.6)", "rgba(200, 200, 200, 0.2)"],
            borderWidth: 1,
          },
        ],
      }
    : {
        labels,
        datasets: [
          {
            label: "Completion Rate (%)",
            data: data.map((d) => d.completionRate),
            backgroundColor: "rgba(75, 192, 192, 0.6)",
          },
        ],
      };

  const activityComparisonData = {
    labels,
    datasets: [
      {
        label: `Total ${activityLabel}`,
        data: data.map((d) => d.totalActivities),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
      {
        label: `Completed ${activityLabel}`,
        data: data.map((d) => d.completedActivities),
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const completionOptions: any = isDoughnut
    ? {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom" as const,
          },
          tooltip: {
            callbacks: {
              label: (context: any) => `${context.label}: ${context.raw}%`,
            },
          },
        },
      }
    : {
        ...options,
        layout: {
          padding: {
            top: showLabels ? 25 : 0, // Extra space for the labels
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
          },
        },
      };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 4 }}>
        <Box sx={{ flex: "1 1 300px", height: 300 }}>
          <Typography variant="subtitle2" align="center">Completion Rate (%)</Typography>
          {isDoughnut ? (
            <Doughnut data={completionRateData as any} options={completionOptions} />
          ) : (
            <Bar 
              data={completionRateData as any} 
              options={completionOptions} 
              plugins={showLabels ? [barDataLabelsPlugin] : []}
            />
          )}
        </Box>
        <Box sx={{ flex: "1 1 300px", height: 300 }}>
          <Typography variant="subtitle2" align="center">{activityLabel} Comparison</Typography>
          <Bar data={activityComparisonData} options={options} />
        </Box>
      </Box>
    </Box>
  );
};

export default HrStatsGraphs;
